/**
 * Chat API Client
 * Unified API client for chatbot endpoints
 */

import { routedFetch } from './api-router.js';

/**
 * Create a new chat session
 * @param {string} worldId - World ID
 * @param {string} userIdentifier - Optional user identifier
 * @returns {Promise<object>} Chat session
 */
export async function createSession(worldId, userIdentifier = 'anonymous') {
  try {
    const data = await routedFetch('/api/chat/sessions', {
      method: 'POST',
      body: JSON.stringify({
        worldId,
        userIdentifier,
      }),
    });

    if (!data.success) {
      throw new Error(data.error || 'Failed to create session');
    }

    return data.session;
  } catch (error) {
    throw new Error(`Failed to create session: ${error.message}`);
  }
}

/**
 * Send a message to the chatbot
 * @param {string} sessionId - Session ID
 * @param {string} message - User message
 * @param {boolean} useRag - Whether to use RAG context
 * @returns {Promise<object>} Assistant response
 */
export async function sendMessage(sessionId, message, useRag = true) {
  try {
    const data = await routedFetch('/api/chat/messages', {
      method: 'POST',
      body: JSON.stringify({
        sessionId,
        message,
        useRag,
      }),
    });

    if (!data.success) {
      throw new Error(data.error || 'Failed to send message');
    }

    return {
      message: data.message,
      ragSources: data.rag_sources,
    };
  } catch (error) {
    throw new Error(`Failed to send message: ${error.message}`);
  }
}

/**
 * Get chat history for a session
 * @param {string} sessionId - Session ID
 * @param {number} limit - Maximum messages to retrieve
 * @returns {Promise<Array<object>>} Chat messages
 */
export async function getChatHistory(sessionId, limit = 50) {
  try {
    const data = await routedFetch(
      `/api/chat/history?sessionId=${sessionId}&limit=${limit}`
    );

    if (!data.success) {
      throw new Error(data.error || 'Failed to get history');
    }

    return data.messages;
  } catch (error) {
    throw new Error(`Failed to get history: ${error.message}`);
  }
}

